import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { AnalyticsLineChart } from '@terminal/modules/analytics-line-chart/AnalyticsLineChart';
import { useThemePerformanceTimeseries } from '@terminal/react-query-hooks/themes/useThemePerformanceTimeseries';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { HorizonSelector } from '@terminal/widgets/theme-widget/tabs/theme-overview-tab/components/horizon-selector/HorizonSelector';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChartTitle,
  isValidHorizon,
  PickedThemePerformanceSortHorizon,
} from './chart-title/ChartTitle';
import { getIntradaySessionWindow, toChartPoints } from './intraday-session';
import styles from './ThemePerformanceSection.module.scss';
import { ThemePerformanceSectionContent } from './ThemePerformanceSectionContent';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

type ThemePerformanceSectionProps = {
  themeId: string;
  entityTags: string[];
  customHeaderDescription?: ReactNode;
  isFetching?: boolean;
  isError?: boolean;
  error?: Error | null;
};

export const ThemePerformanceSection = ({
  themeId,
  entityTags,
  customHeaderDescription,
  isFetching,
  isError,
  error = null,
}: ThemePerformanceSectionProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );

  const { params, setQueryParams } = useQueryParams();
  const defaultHorizon: PickedThemePerformanceSortHorizon =
    isIntradayChartEnabled ? 'intraday' : '1y';
  const themeHorizonParam = params[queryKeys.themeHorizon];
  const isHorizonParamUsable =
    isValidHorizon(themeHorizonParam) &&
    (themeHorizonParam === 'intraday' ? isIntradayChartEnabled : true);
  // The horizon is only driven by search params when the drawer was opened
  // with one already present (e.g. deep-linked). Otherwise it's local state,
  // so the drawer works the same when opened without search params (e.g.
  // from ManageFollowingThemesTab).
  const isHorizonControlledByQueryParams = useRef(isHorizonParamUsable).current;
  const [horizon, setHorizon] = useState<PickedThemePerformanceSortHorizon>(
    () => (isHorizonParamUsable ? themeHorizonParam : defaultHorizon)
  );

  const handleHorizonChange = (
    newHorizon: PickedThemePerformanceSortHorizon
  ) => {
    setHorizon(newHorizon);
    if (isHorizonControlledByQueryParams) {
      setQueryParams({ [queryKeys.themeHorizon]: newHorizon });
    }
  };

  const {
    data: themePerformance,
    isFetching: isThemePerformanceFetching,
    isError: isThemePerformanceError,
    error: themePerformanceError,
  } = useThemePerformanceTimeseries({ themeId, horizon }, !!themeId);

  const aggregate = themePerformance?.aggregate;

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const isIntraday = horizon === 'intraday';
  const { fromDate, toDate } = isIntraday
    ? getIntradaySessionWindow(aggregate?.[0]?.index)
    : { fromDate: undefined, toDate: undefined };

  return (
    <ThemePerformanceSectionContent
      isLoading={isThemePerformanceFetching || !!isFetching}
      isError={isThemePerformanceError || !!isError}
      isEmpty={!aggregate?.length}
      error={!!isError ? error : themePerformanceError}
    >
      <div className={styles.chartAndHorizonsWrapper}>
        <HorizonSelector
          activeHorizon={horizon}
          onChange={handleHorizonChange}
        />
        <ChartTitle
          activeHorizon={horizon}
          horizonReturnValue={aggregate?.at(-1)?.value}
          description={
            customHeaderDescription ??
            `${t('themeOverview.equalWeightedBasket')} ${t(
              'themeOverview.topCompanies',
              { total }
            )}`
          }
        />
        <div className={styles.chartWrapper}>
          <AnalyticsLineChart
            multiLineConfig={{
              aggregate: [
                {
                  label: t('themeOverview.themePerformance'),
                  color: colors.default.value,
                  data: toChartPoints(aggregate, isIntraday),
                },
              ],
            }}
            fromDate={fromDate}
            toDate={toDate}
            includeTime={isIntraday}
          />
        </div>
      </div>
    </ThemePerformanceSectionContent>
  );
};
